import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from "recharts";
import { Sparkles, Users, IndianRupee, CalendarRange, TrendingDown, ClipboardCheck } from "lucide-react";
import { api } from "@/api";
import { formatINR } from "@/lib/risk";

function Stat({ icon: Icon, label, value, accent, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      className="rounded-2xl border border-border bg-white p-5 card-shadow" data-testid={`insight-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${accent}18` }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </span>
      <div className="mt-3 font-display text-2xl font-bold text-ink">{value}</div>
      <div className="text-sm text-ink-muted">{label}</div>
    </motion.div>
  );
}

const TIP = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-border bg-white px-3 py-2 text-xs card-shadow">
      <div className="font-semibold text-ink">{label}</div>
      <div className="text-ink-muted">{payload[0].name}: {payload[0].value}</div>
    </div>
  ) : null;

export default function Insights() {
  const [d, setD] = useState(null);
  useEffect(() => { api.insights().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="py-24 text-center text-ink-muted">Loading insights…</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-brand">
          <Sparkles className="h-4 w-4" /> Business Impact
        </div>
        <h1 className="font-display text-3xl font-bold text-ink">Retention Impact</h1>
        <p className="text-ink-muted">Turning early intervention into measurable, protected revenue.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat i={0} icon={Users} label="Students Saved" value={d.students_saved} accent="#10B981" />
        <Stat i={1} icon={IndianRupee} label="Monthly Revenue Protected" value={formatINR(d.revenue_protected)} accent="#4F46E5" />
        <Stat i={2} icon={CalendarRange} label="Annualized Protected" value={formatINR(d.annualized_protected)} accent="#4F46E5" />
        <Stat i={3} icon={TrendingDown} label="Avg Risk Reduction" value={`${d.avg_risk_reduction}%`} accent="#0EA5E9" />
        <Stat i={4} icon={ClipboardCheck} label="Interventions Completed" value={d.interventions_completed} accent="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="chart-risk-trend">
          <h3 className="font-display text-lg font-bold text-ink">Risk Trend</h3>
          <p className="text-sm text-ink-muted">Average cohort risk score over recent weeks.</p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.risk_trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F6" vertical={false} />
                <XAxis dataKey="week" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                <Tooltip content={<TIP />} />
                <Line type="monotone" dataKey="risk" name="Avg risk" stroke="#4F46E5" strokeWidth={3} dot={{ r: 4, fill: "#4F46E5" }} activeDot={{ r: 6 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="chart-recovered">
          <h3 className="font-display text-lg font-bold text-ink">Recovered Students Over Time</h3>
          <p className="text-sm text-ink-muted">Students successfully re-engaged each month.</p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.recovered_trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F6" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip content={<TIP />} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="count" name="Recovered" fill="#10B981" radius={[6, 6, 0, 0]} barSize={38} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="top-signals">
        <h3 className="font-display text-lg font-bold text-ink">Top Dropout Signals</h3>
        <p className="text-sm text-ink-muted">Which signals most often precede student risk.</p>
        <div className="mt-5 space-y-4">
          {d.top_signals.map((s) => (
            <div key={s.signal}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-ink-soft">{s.signal}</span>
                <span className="font-bold text-ink">{s.pct}%</span>
              </div>
              <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-secondary">
                <motion.div initial={{ width: 0 }} animate={{ width: `${s.pct}%` }} transition={{ duration: 0.9, ease: "easeOut" }}
                  className="h-full rounded-full bg-indigo-brand" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-ink p-6 text-white card-shadow-lg" data-testid="insight-note">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-brand/30 blur-3xl" />
        <div className="relative flex items-start gap-3">
          <Sparkles className="mt-1 h-5 w-5 shrink-0 text-indigo-200" />
          <div>
            <div className="text-sm font-semibold text-indigo-200">BatchPulse Insight</div>
            <p className="mt-1 font-display text-xl font-semibold leading-relaxed">{d.ai_note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
